/**
 * Esito della creazione di un pagamento (devops#431).
 *
 * Con totale 0 il server completa l'ordine da sé: `PaymentHandlingService.handleFullWalletPayment` risponde
 * `{status: 'completed', remainingAmount: 0, ...}` da `paypal/create` come da `stripe/create`, senza `approveUrl`,
 * `orderId` né `client_secret`. La pagina, che li pretendeva, mostrava un errore su un ordine in realtà completato:
 * il socio riprovava, e il secondo tentativo — con il codice ormai consumato — nasceva a prezzo pieno.
 *
 * Qui sta la sola lettura di quelle risposte, così PayPal, carta e Apple/Google Pay non possono divergere.
 * Le funzioni non toccano né il DOM né il router: ricevono ciò che serve e restituiscono un esito.
 */

/**
 * La risposta dice che l'ordine è già completato, senza altro da fare per il socio.
 * Un `completed` con un residuo ancora da pagare non è un completamento.
 */
export function rispostaCompletata(data) {
    if (!data || typeof data !== 'object') return false;
    if (String(data.status ?? '').toLowerCase() !== 'completed') return false;
    return !(Number(data.remainingAmount ?? 0) > 0);
}

/**
 * paypal/create: con `approveUrl` e `orderId` si apre PayPal (come prima); con «completato» si va al successo;
 * «dati non validi» resta solo per le risposte davvero incomplete. L'approvazione ha la precedenza.
 */
export function esitoCreazionePayPal(data) {
    if (data && data.approveUrl && data.orderId) {
        return { tipo: 'approvazione', approveUrl: data.approveUrl, orderId: data.orderId };
    }
    if (rispostaCompletata(data)) return { tipo: 'completato' };
    return { tipo: 'non_valida' };
}

/**
 * stripe/create: con `client_secret` si conferma con Stripe (come prima); con «completato» non c'è nulla da
 * confermare e si va al successo.
 */
export function esitoCreazioneCarta(data) {
    if (data && data.client_secret) return { tipo: 'conferma', clientSecret: data.client_secret };
    if (rispostaCompletata(data)) return { tipo: 'completato' };
    return { tipo: 'non_valida' };
}

/** `chiama` esegue la fetch e restituisce la Response: il componente decide url, header e corpo. */
export async function creaPagamentoCarta(chiama) {
    const response = await chiama();
    if (!response.ok) {
        throw new Error('Failed to create payment intent');
    }
    return esitoCreazioneCarta(await response.json());
}

export async function creaPagamentoPayPal(chiama) {
    const response = await chiama();
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Errore PayPal: ' + errorText);
    }
    return esitoCreazionePayPal(await response.json());
}

const ERRORE_CARTA = 'Si è verificato un errore durante il pagamento.';

/**
 * Pagamento con carta: crea il pagamento e, solo se il server lo chiede, lo conferma con Stripe.
 *
 * @param {object} p
 * @param {() => Promise<Response>} p.chiama       POST stripe/create
 * @param {(clientSecret: string) => Promise<{error?: {message: string}, paymentIntent?: {status: string}}>} p.confermaStripe
 * @param {(metodo: string) => void} p.vaiAlSuccesso
 * @param {(messaggio: string) => void} p.mostraErrore
 * @returns {Promise<'completato'|'riuscito'|'in_corso'|'errore'>}
 */
export async function pagaConCarta({ chiama, confermaStripe, vaiAlSuccesso, mostraErrore }) {
    try {
        const esito = await creaPagamentoCarta(chiama);
        if (esito.tipo === 'completato') {
            vaiAlSuccesso('STRIPE');
            return 'completato';
        }
        if (esito.tipo !== 'conferma') {
            mostraErrore(ERRORE_CARTA);
            return 'errore';
        }
        const { error, paymentIntent } = await confermaStripe(esito.clientSecret);
        if (error) {
            mostraErrore(error.message);
            return 'errore';
        }
        if (paymentIntent && paymentIntent.status === 'succeeded') {
            vaiAlSuccesso('STRIPE');
            return 'riuscito';
        }
        return 'in_corso';
    } catch (e) {
        console.error(e);
        mostraErrore(ERRORE_CARTA);
        return 'errore';
    }
}

/**
 * Avvio di PayPal: restituisce l'esito. Solo `approvazione` richiede che il chiamante apra la finestra PayPal e
 * attenda lo stato; `completato` ha già portato al successo, gli altri hanno già mostrato l'errore.
 *
 * @returns {Promise<{tipo: 'approvazione', approveUrl: string, orderId: string}|{tipo: 'completato'|'non_valida'|'errore'}>}
 */
export async function avviaPayPal({ chiama, vaiAlSuccesso, mostraErrore }) {
    try {
        const esito = await creaPagamentoPayPal(chiama);
        if (esito.tipo === 'completato') {
            vaiAlSuccesso('PAYPAL');
        } else if (esito.tipo === 'non_valida') {
            mostraErrore('Errore: dati PayPal non validi');
        }
        return esito;
    } catch (e) {
        console.error(e);
        mostraErrore('Errore durante il pagamento con PayPal');
        return { tipo: 'errore' };
    }
}

/**
 * Giudizio sul ricontrollo del codice sconto fatto PRIMA di chiedere il pagamento (GET /api/promo/validate).
 *
 * Il codice si consuma solo a ordine completato. Se un ordine è andato a buon fine ma la pagina non l'ha saputo,
 * il riepilogo continua a mostrare lo sconto mentre il server lo rifiuta: il nuovo ordine nasce a prezzo pieno e
 * il socio lo scopre a cose fatte. Chiedere al server prima permette di dirlo e di mostrare il prezzo vero.
 *
 * Solo una risposta di rifiuto del server (400 con status «error») toglie lo sconto. Limite di richieste,
 * sessione scaduta, errori del server o rete assente NON lo tolgono: a decidere resta il pagamento stesso, e un
 * guasto del ricontrollo non deve fermare un acquisto legittimo.
 *
 * @returns {{tipo: 'valido'} | {tipo: 'non_verificabile'} | {tipo: 'non_valido', messaggio: string, giaUsato: boolean}}
 */
export function giudicaRicontrolloCodice(httpStatus, body) {
    if (httpStatus >= 200 && httpStatus < 300) {
        if (body && body.status !== 'error' && body.promoCode) return { tipo: 'valido' };
        return { tipo: 'non_verificabile' };
    }
    if (httpStatus === 400 && body && body.status === 'error') {
        const messaggio = String(body.message || 'Il codice sconto non è più valido.');
        return { tipo: 'non_valido', messaggio, giaUsato: /già\s+(utilizzat|usat)/i.test(messaggio) };
    }
    return { tipo: 'non_verificabile' };
}

/** Per quanto tempo un carrello già completato viene ricordato: un ripensamento, non una scelta d'acquisto. */
export const FINESTRA_CARRELLO_COMPLETATO_MS = 30 * 60 * 1000;

const CHIAVE_CARRELLO_COMPLETATO = 'ef_checkout_completato';

/**
 * Impronta del carrello: ciò che rende «lo stesso acquisto» due richieste di pagamento. Non include il metodo di
 * pagamento (cambiare metodo dopo un acquisto riuscito è comunque lo stesso acquisto) né token e dati anagrafici.
 */
export function firmaCarrello(richiesta) {
    const r = richiesta || {};
    const upsells = Array.isArray(r.upsells) ? [...r.upsells].map(String).sort() : [];
    return JSON.stringify({
        utente: String(r.email ?? '').trim().toLowerCase(),
        prodotto: r.productId ?? null,
        variazione: String(r.variationName ?? '').trim().toLowerCase(),
        promo: String(r.promoCode ?? '').trim().toLowerCase(),
        upsells,
        sede: r.centerId ?? null,
        listino: r.pricingCenterId ?? null,
        inizio: r.customStartDate ?? null,
        regalo: r.isGift === true ? String(r.giftRecipientEmail ?? '').trim().toLowerCase() : null,
    });
}

function leggi(storage) {
    try {
        const grezzo = storage && storage.getItem(CHIAVE_CARRELLO_COMPLETATO);
        const letto = grezzo ? JSON.parse(grezzo) : null;
        return letto && typeof letto === 'object' ? letto : null;
    } catch {
        return null;
    }
}

/** Ricorda che questo carrello si è concluso. Lo storage può mancare o rifiutare: in quel caso non si ricorda. */
export function registraCarrelloCompletato(storage, firma, metodo, ora = Date.now()) {
    try {
        storage.setItem(CHIAVE_CARRELLO_COMPLETATO, JSON.stringify({ firma, metodo: metodo || null, ora }));
    } catch {
        // Nessuna memoria: il server resta l'ultima difesa, la pagina non si ferma per questo.
    }
}

/**
 * C'è un carrello ricordato e ancora nella finestra? Serve a non costruire l'impronta (che passa dalla richiesta di
 * pagamento, con i suoi avvisi) quando non c'è nulla con cui confrontarla.
 */
export function haCarrelloRicordato(storage, ora = Date.now()) {
    const letto = leggi(storage);
    return !!letto && ora - Number(letto.ora) <= FINESTRA_CARRELLO_COMPLETATO_MS;
}

/** @returns {{metodo: string|null} | null} il metodo con cui si è concluso, o null se non risulta completato */
export function carrelloGiaCompletato(storage, firma, ora = Date.now()) {
    const letto = leggi(storage);
    if (!letto || letto.firma !== firma) return null;
    if (!(ora - Number(letto.ora) <= FINESTRA_CARRELLO_COMPLETATO_MS)) return null;
    return { metodo: letto.metodo ?? null };
}

/** Dimentica il carrello concluso: chi sceglie di acquistare di nuovo non deve rivedere l'avviso a ogni clic. */
export function dimenticaCarrelloCompletato(storage) {
    try {
        storage.removeItem(CHIAVE_CARRELLO_COMPLETATO);
    } catch {
        // niente da fare
    }
}
